// We're getting a bunch of html from the server and shoving it into react here.

import * as React from 'react';
import {ApolloProvider} from 'react-apollo';
import ReactDOM from 'react-dom';
import {Helmet} from 'react-helmet';
import {Provider} from 'react-redux';
import {Prompt, Router} from 'react-router-dom';

import {ApolloProvider as ReactHooksApolloProvider} from '@apollo/react-hooks';
import _ from 'underscore';
// @ts-expect-error
import capitalize from 'underscore.string/capitalize';

import {Api} from 'nutshell-core/api';

import {headTitle} from 'shells/document/head-title';

import {nutApolloClient} from '../../apollo-client';
import {appHistory} from '../../history';
import {store} from '../../store';
import {ConstantContactPreviewListener} from '../../ui/core/constant-contact/constant-contact-email-preview/constant-contact-preview-listener';
import {TimelineSendEmailButton} from '../../ui/email/timeline-send-email-button';
import {FileAttachments} from '../../ui/entity-details/files/file-attachments';
import {FileSection} from '../../ui/entity-details/files/file-section';
import {PeepAvatarFooterWrapper} from '../../ui/entity-details/peep-avatar-footer-wrapper';
import {PeepTopRightInfo} from '../../ui/entity-details/peep-top-right-info';
import {OnboardingPhone} from '../../ui/phone-call';
import {getRouteChangeBlockingMessage} from '../../util/get-route-change-blocking-message';

import {MarketingEmailPreviewListener} from '../core/email-marketing/marketing-email-preview/marketing-email-preview-listener';
import {PeepTags} from '../core/tags/peep-tags';
import {PeepDetailsSection} from '../entity-details/peep-details-section';
import {PageWrapper} from '../layout/page-wrapper';
import {CompanyEnrichmentWrapper} from '../nutshell-intelligence-enrichment/company-enrichment/company-enrichment-wrapper';
import {ContactEnrichmentWrapper} from '../nutshell-intelligence-enrichment/contact-enrichment/contact-enrichment-wrapper';
import {PeopleIqBadge} from '../people-iq/people-iq-badge';
import {EntityPageTimeline} from '../timeline/entity-page-timeline';

import {AccountHierarchy} from './account-hierarchy';
import {ActivitiesSection} from './activities/activities-section';
import {ConstantContactRollup} from './constant-contact/constant-contact-rollup';
import {CompanyDripSequenceProgressionSection} from './email-marketing/drip-sequence-progression/company-drip-sequence-progression-section';
import {PersonDripSequenceProgressionSection} from './email-marketing/drip-sequence-progression/person-drip-sequence-progression-section';
import {EntityPageEmailSequencesSection} from './email-marketing/email-sequences/entity-page-email-sequences-section';
import {PeepEmailMarketingSection} from './email-marketing/peep-email-marketing-section';
import {FormSubmissionsSection} from './form-submissions/form-submissions-section';
import {PeepContents} from './peep-contents';

import styles from './peep-page.module.css';

export const TIMELINE_REACT_MOUNT_ID = 'timeline-react-mount';
export const PEOPLE_IQ_REACT_MOUNT_ID = 'people-iq-react-mount';
export const ACTIVITIES_REACT_MOUNT_ID = 'activities-react-mount';
const SELECTOR = '#peep-page';

const viewApi = new Api('/');
const headers = new Headers({
    'Content-Type': 'application/xhtml+xml',
    accept: '*/*',
    'X-Requested-With': 'XMLHttpRequest',
});

type Props = {
    peepSlug: string;
    entityType: 'person' | 'company';
};

type State = {
    peepMarkup: HTMLElement | null | undefined;
    peepName: string | null | undefined;
    didRenderPeepMarkup: boolean;
    isLoading: boolean;
};

function showPhoneOnboarding() {
    const onboardingPhoneModal = document.querySelector('#onboarding-phone-modal');
    if (onboardingPhoneModal) {
        _.delay(() => onboardingPhoneModal.classList.add('state-active'), 500);

        const handleModalClose = () => {
            onboardingPhoneModal.classList.remove('state-active');

            // Remove the modal entirely, so we don't pop up if they add another phone number this page load
            _.delay(() => onboardingPhoneModal.remove(), 500);
        };

        ReactDOM.render(<OnboardingPhone onModalClose={handleModalClose} />, onboardingPhoneModal);
    }
}

document.addEventListener('Peep initialized', () => {
    Peep.currentPeep.on('add:phones', showPhoneOnboarding);
    const fileSectionMount = document.querySelector('.peep-file-section');
    if (fileSectionMount) {
        ReactDOM.render(<FileSection model={Peep.currentPeep} />, fileSectionMount);
    }

    const accountHierarchyMount = document.querySelector('#account-hierarchy-react-mount');
    if (accountHierarchyMount) {
        ReactDOM.render(
            <Provider store={store}>
                <ApolloProvider client={nutApolloClient}>
                    <ReactHooksApolloProvider client={nutApolloClient}>
                        <Router history={appHistory}>
                            <AccountHierarchy accountId={Peep.currentPeep.id} />
                        </Router>
                    </ReactHooksApolloProvider>
                </ApolloProvider>
            </Provider>,
            accountHierarchyMount
        );
    }

    const emailCreatorFileSection = document.querySelector('.creator-email-file-section');
    if (emailCreatorFileSection) {
        ReactDOM.render(<FileAttachments model={Timeline.creatorEmail} />, emailCreatorFileSection);
    }

    const timelineCreatorEmailButton = document.querySelector(
        '#timeline-creator-emails-button-react-mount'
    );
    if (timelineCreatorEmailButton) {
        const mount = document.createElement('div');
        timelineCreatorEmailButton.appendChild(mount);

        ReactDOM.render(
            <Provider store={store}>
                <TimelineSendEmailButton
                    entityId={Peep.currentPeep.id}
                    buttonNode={timelineCreatorEmailButton}
                />
            </Provider>,
            mount
        );
    }
});

export class PeepPage extends React.Component<Props, State> {
    timeline: any | null | undefined;
    scrollContainerRef: HTMLDivElement | null | undefined;

    constructor(props: Props) {
        super(props);
        this.state = {
            peepMarkup: null,
            peepName: null,
            didRenderPeepMarkup: false,
            isLoading: true,
        };
    }

    componentDidMount() {
        document.addEventListener('Peep initialized', this.forceRender);
        // If an email was added or deleted, we need to re-render so we show/hide the constant contact section as needed
        $('body').on(Nut.events.global.entityWasEdited, this.forceRender);
        $('body').on(Nut.events.global.accountContactCardsWereReloaded, this.reloadPeopleIqBadge);
        this.getPeepMarkup(this.props.peepSlug, this.props.entityType);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.peepSlug !== this.props.peepSlug) {
            Peep.unloadPeep();
            this.getPeepMarkup(nextProps.peepSlug, nextProps.entityType);
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (!prevState.didRenderPeepMarkup && this.state.didRenderPeepMarkup) {
            Nut.preparePage();
            Nut.updateAppearances();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('Peep initialized', this.forceRender);
        $('body').off(Nut.events.global.entityWasEdited, this.forceRender);
        $('body').off(Nut.events.global.accountContactCardsWereReloaded, this.reloadPeopleIqBadge);
        Peep.unloadPeep();
    }

    forceRender = () => {
        // Force another render after peep is initialized and backbone templates are rendered
        this.forceUpdate();
    };

    render() {
        const peepHeaderMount = document.querySelector('#peep-header-react-mount');
        const peepTagsMount = document.querySelector('#peep-tags-react-mount');
        const companyEnrichmentMount = document.querySelector('#company-enrichment-react-mount');
        const contactEnrichmentMount = document.querySelector('#contact-enrichment-react-mount');
        const peepAccountDetailsMount = document.querySelector('#peep-account-details-react-mount');
        const peepSendToMount = document.querySelector('.send-to-button');
        const ctctRollupMount = document.querySelector('#constant-contact-rollup-mount');
        const ctctEmailPreviewMount = document.querySelector('#constant-contact-peep-react-mount');
        const marketingEmailPreviewMount = document.querySelector(
            '#email-marketing-peep-react-mount'
        );
        const audienceInfoMount = document.querySelector('#audience-info-react-mount');
        const formSubmissionsMount = document.querySelector('#form-submissions-react-mount');
        const dripSequenceProgressionMount = document.querySelector(
            '#drip-sequence-progression-react-mount'
        );
        const personalEmailSequencesMount = document.querySelector(
            '#personal-email-sequences-react-mount'
        );
        const timelineReactMount = document.querySelector(`#${TIMELINE_REACT_MOUNT_ID}`);
        const peopleIqReactMount = document.querySelector(`#${PEOPLE_IQ_REACT_MOUNT_ID}`);
        const activitiesReactMount = document.querySelector(`#${ACTIVITIES_REACT_MOUNT_ID}`);

        return (
            <PageWrapper>
                <div
                    ref={(node) => {
                        this.scrollContainerRef = node;
                    }}
                    className={styles.container}
                >
                    <Prompt
                        // As documented here: https://reactrouter.com/core/api/Prompt, you can pass a function as the message
                        // that returns a string to show a prompt to the user or true to allow the transition.
                        // Here we want to warn users if they try to leave the page before saving their timeline input.
                        message={(location, action) => {
                            // This check is for flow - action should always exist
                            if (action) {
                                const blockingMessage = getRouteChangeBlockingMessage(action);
                                if (blockingMessage) {
                                    return blockingMessage;
                                }

                                return true;
                            }

                            return true;
                        }}
                    />
                    <Helmet>
                        {this.state.peepName ? (
                            <title>
                                {headTitle([
                                    capitalize(this.props.entityType),
                                    this.state.peepName,
                                ])}
                            </title>
                        ) : undefined}
                    </Helmet>
                    <PeepContents
                        peepMarkup={this.state.peepMarkup}
                        isLoading={this.state.isLoading}
                        onMarkupRendered={() => {
                            if (!this.state.didRenderPeepMarkup) {
                                this.setState({didRenderPeepMarkup: true});
                            }
                        }}
                    />
                    {/* Required for dragging file uploads */}
                    <div className='drag-drop-overlay'>
                        <div className='drag-drop-target-container' />
                    </div>
                    {peepHeaderMount && Peep.currentPeep
                        ? ReactDOM.createPortal(
                              <PeepTopRightInfo
                                  peepApiId={Peep.currentPeep.id}
                                  model={Peep.currentPeep}
                              />,
                              peepHeaderMount
                          )
                        : null}
                    {peepTagsMount && Peep.currentPeep
                        ? ReactDOM.createPortal(
                              <Provider store={store}>
                                  <ApolloProvider client={nutApolloClient}>
                                      <ReactHooksApolloProvider client={nutApolloClient}>
                                          <PeepTags
                                              id={Peep.currentPeep.id}
                                              entityType={this.props.entityType}
                                          />
                                      </ReactHooksApolloProvider>
                                  </ApolloProvider>
                              </Provider>,
                              peepTagsMount
                          )
                        : null}
                    {companyEnrichmentMount && Peep.currentPeep
                        ? ReactDOM.createPortal(
                              <CompanyEnrichmentWrapper
                                  accountId={Peep.currentPeep.id}
                                  companyName={this.state.peepName}
                              />,
                              companyEnrichmentMount
                          )
                        : null}
                    {contactEnrichmentMount && Peep.currentPeep
                        ? ReactDOM.createPortal(
                              <ContactEnrichmentWrapper
                                  contactId={Peep.currentPeep.id}
                                  contactName={this.state.peepName}
                              />,
                              contactEnrichmentMount
                          )
                        : null}
                    {peepAccountDetailsMount && Peep.currentPeep
                        ? ReactDOM.createPortal(
                              <ApolloProvider client={nutApolloClient}>
                                  <ReactHooksApolloProvider client={nutApolloClient}>
                                      <Router history={appHistory}>
                                          <PeepDetailsSection
                                              peepApiId={Peep.currentPeep.id}
                                              model={Peep.currentPeep}
                                          />
                                      </Router>
                                  </ReactHooksApolloProvider>
                              </ApolloProvider>,
                              peepAccountDetailsMount
                          )
                        : null}
                    {peepSendToMount && Peep.currentPeep
                        ? ReactDOM.createPortal(
                              <PeepAvatarFooterWrapper peepId={Peep.currentPeep.id} />,
                              peepSendToMount
                          )
                        : null}
                    {ctctRollupMount &&
                    Peep.currentPeep &&
                    Peep.currentPeep.attributes.emails.length // Only add constant contact section if they have an email address
                        ? ReactDOM.createPortal(
                              <ConstantContactRollup
                                  entityId={Peep.currentPeep.id}
                                  entityName={Peep.currentPeep.get('name')}
                                  model={Peep.currentPeep}
                              />,
                              ctctRollupMount
                          )
                        : null}
                    {ctctEmailPreviewMount
                        ? ReactDOM.createPortal(
                              <ConstantContactPreviewListener />,
                              ctctEmailPreviewMount
                          )
                        : null}
                    {marketingEmailPreviewMount
                        ? ReactDOM.createPortal(
                              <MarketingEmailPreviewListener />,
                              marketingEmailPreviewMount
                          )
                        : null}
                    {audienceInfoMount && Peep.currentPeep
                        ? ReactDOM.createPortal(
                              <PeepEmailMarketingSection contactId={Peep.currentPeep.id} />,
                              audienceInfoMount
                          )
                        : null}
                    {formSubmissionsMount && Peep.currentPeep
                        ? ReactDOM.createPortal(
                              <FormSubmissionsSection contactId={Peep.currentPeep.id} />,
                              formSubmissionsMount
                          )
                        : null}
                    {dripSequenceProgressionMount &&
                    Peep.currentPeep &&
                    this.props.entityType === 'person'
                        ? ReactDOM.createPortal(
                              <PersonDripSequenceProgressionSection
                                  contactId={Peep.currentPeep.id}
                              />,
                              dripSequenceProgressionMount
                          )
                        : null}
                    {dripSequenceProgressionMount &&
                    Peep.currentPeep &&
                    this.props.entityType === 'company'
                        ? ReactDOM.createPortal(
                              <CompanyDripSequenceProgressionSection
                                  accountId={Peep.currentPeep.id}
                              />,
                              dripSequenceProgressionMount
                          )
                        : null}
                    {personalEmailSequencesMount && Peep.currentPeep
                        ? ReactDOM.createPortal(
                              <EntityPageEmailSequencesSection
                                  entityId={Peep.currentPeep.id}
                                  entityType={this.props.entityType}
                              />,
                              personalEmailSequencesMount
                          )
                        : null}
                    {timelineReactMount && Peep.currentPeep
                        ? ReactDOM.createPortal(
                              <EntityPageTimeline
                                  entityId={Peep.currentPeep.id}
                                  entityName={
                                      this.props.entityType === 'company' ? 'Accounts' : 'Contacts'
                                  }
                                  entityPageName={this.state.peepName}
                                  scrollContainerRef={this.scrollContainerRef}
                              />,
                              timelineReactMount
                          )
                        : null}
                    {peopleIqReactMount && Peep.currentPeep && this.props.entityType === 'company'
                        ? ReactDOM.createPortal(
                              <PeopleIqBadge
                                  accountId={Peep.currentPeep.id}
                                  accountName={Peep.currentPeep.get('name')}
                              />,
                              peopleIqReactMount
                          )
                        : null}
                    {activitiesReactMount && Peep.currentPeep
                        ? ReactDOM.createPortal(
                              <ActivitiesSection entityId={Peep.currentPeep.id} />,
                              activitiesReactMount
                          )
                        : null}
                </div>
            </PageWrapper>
        );
    }

    getPeepMarkup = (peepSlug: string, entityType: string) => {
        const peepId = peepSlug.split('-')[0];
        const url = `/${entityType}/${peepId}.webchunk`;

        this.setState({isLoading: true});

        viewApi
            // @ts-expect-error
            .get(url, undefined, headers)
            .then((res) => {
                // @ts-expect-error
                if (res && res.redirected) {
                    // @ts-expect-error
                    window.location.href = res.url;
                }

                return res;
            })
            // @ts-expect-error
            .then((res) => res.text())
            .then((body) => {
                const parser = new DOMParser();

                // Parse the text
                const doc = parser.parseFromString(body, 'text/html');

                const peepMarkup = doc.querySelector(SELECTOR);
                const peepContainer = peepMarkup && peepMarkup.querySelector('.peep-container');

                if (peepMarkup && peepContainer) {
                    // @ts-expect-error
                    const peepApiId = peepContainer.dataset.entityId;
                    // @ts-expect-error
                    const peepName = peepContainer.dataset.displayName;
                    // @ts-expect-error
                    const peepType = peepContainer.dataset.entityName;

                    // Put the Peep model on `Peep.currentPeep` for legacy code
                    // @ts-expect-error
                    window.Peep.currentPeep = Nut.Model[peepType].findOrCreate({
                        id: `${peepApiId}-${peepType.toLowerCase()}`,
                    });
                    this.setState({
                        // @ts-expect-error
                        peepMarkup: peepMarkup,
                        peepName: peepName,
                        // Reset this, so we can detect the change when loading a new peep
                        didRenderPeepMarkup: false,
                        isLoading: false,
                    });
                }
            });
        // TODO: handle errors
    };

    reloadPeopleIqBadge = () => {
        const peopleIqReactMount = document.querySelector(`#${PEOPLE_IQ_REACT_MOUNT_ID}`);

        if (peopleIqReactMount && Peep.currentPeep && this.props.entityType === 'company') {
            ReactDOM.createPortal(
                <Provider store={store}>
                    <ApolloProvider client={nutApolloClient}>
                        <ReactHooksApolloProvider client={nutApolloClient}>
                            <PeopleIqBadge
                                accountId={Peep.currentPeep.id}
                                accountName={Peep.currentPeep.get('name')}
                            />
                        </ReactHooksApolloProvider>
                    </ApolloProvider>
                </Provider>,
                peopleIqReactMount
            );
        }
    };
}
